import {
  createFileRoute,
  Outlet,
  redirect,
  useLoaderData,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { SidebarMenu } from "~/components/Admin/SidebarMenu";
import { TopPanel } from "~/components/Admin/TopPanel";
import { Button } from "~/components/ui/button";
import { FormIcon } from "~/icons/FormIcon";
import { HeartPlusIcon } from "~/icons/HeartPlus";
import { PasswordIcon } from "~/icons/PasswordIcon";
import { PinIcon } from "~/icons/PinIcon";
import { UserIcon } from "~/icons/UserIcon";
import { getUserSession } from "~/lib/api/auth.api";
import { authQueries } from "~/lib/queries";
import { getFooter } from "~/lib/server-functions/get-footer";
import { useAdvertiserInquiryCount } from "~/lib/tanstack-query/hooks/useAdvertiserInquiryCount";
import { useTranslations } from "~/lib/tanstack-query/hooks/useTranslation";
import { ADD_LOCATION_TIPS_NAMESPACE } from "~/lib/add-location/components/LocationForm";
import { TranslationContent } from "~/components/TranslationContent";
import { IMAGE_SIZES, buildImageUrl, cn } from "~/lib/utils";
import { withLocale } from "~/lib/utils/with-locale";
import { Footer } from "~/routes/__main/route";
import { MenuItem } from "~/types/menu";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { InfoIcon } from "lucide-react";

export const Route = createFileRoute("/advertiser/_assistant")({
  beforeLoad: async ({ location }) => {
    const sessionData = await getUserSession();

    if (!sessionData?.user) {
      throw redirect({
        to: withLocale("/sign-in"),
        search: location.pathname ? { redirect: location.pathname } : undefined,
      });
    }

    if (!sessionData.user.isAdvertiser) {
      throw redirect({
        to: withLocale("/sign-in"),
        search: location.pathname ? { redirect: location.pathname } : undefined,
      });
    }
  },
  component: RouteComponent,
  loader: async ({ context }) => {
    // Prefetch auth session server-side so it's available immediately
    const userSession = await getUserSession();
    context.queryClient.setQueryData(authQueries.user().queryKey, userSession);

    return await getFooter();
  },
});

function RouteComponent() {
  const footer = useLoaderData({ from: "/advertiser/_assistant" });
  const location = useLocation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveButtonLabel, setSaveButtonLabel] = useState("Speichern");
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const { data: inquiryCount = 0 } = useAdvertiserInquiryCount();
  const { data: formTranslations } = useTranslations(
    ADD_LOCATION_TIPS_NAMESPACE,
    ["formHeader.subtitle", "formHeader.tip"],
  );

  const menuItems: MenuItem[] = [
    {
      label: "Benutzerkonto",
      icon: <UserIcon />,
      items: [
        {
          label: "Benutzerkonto bearbeiten",
          to: withLocale("/user"),
          icon: <FormIcon />,
        },
        {
          label: "Passwort ändern",
          to: withLocale("/password"),
          icon: <PasswordIcon />,
        },
        {
          label: "Rechnungsdaten",
          to: withLocale("/billing"),
          icon: <FormIcon />,
        },
      ],
    },
    {
      label: "Meine Locations",
      to: withLocale("/locations"),
      icon: <PinIcon />,
    },
    {
      label: "Meine Anfragen",
      to: withLocale("/inquiries"),
      icon: <HeartPlusIcon />,
      badge: inquiryCount,
    },
  ];

  // Keep label in sync with route (add vs edit); form can override via data-save-label when it mounts
  useEffect(() => {
    const isEdit = location.pathname.includes("edit-location");
    setSaveButtonLabel(isEdit ? "Speichern" : "Speichern und weiter");
  }, [location.pathname]);

  useEffect(() => {
    const form = document.getElementById("location-form");
    if (!form) return;

    const observer = new MutationObserver(() => {
      const submitting = form.getAttribute("data-submitting") === "true";
      setIsSubmitting(submitting);
      const label = form.getAttribute("data-save-label");
      if (label) setSaveButtonLabel(label);
    });

    // Initial check
    setIsSubmitting(form.getAttribute("data-submitting") === "true");
    const initialLabel = form.getAttribute("data-save-label");
    if (initialLabel) setSaveButtonLabel(initialLabel);

    // Observe changes to data-submitting and data-save-label
    observer.observe(form, {
      attributes: true,
      attributeFilter: ["data-submitting", "data-save-label"],
    });

    return () => observer.disconnect();
  }, [location.pathname]);

  return (
    <>
      <TopPanel menuIsOpen={menuIsOpen} onMenuToggle={setMenuIsOpen} />
      <div className="pt-2 xl:pt-16 layout-content-grid max-w-(--layout-page-max-width) mx-auto ring ring-gray-200 shadow-2xl min-h-[calc(100vh-var(--top-panel-height))] grid-rows-[auto_1fr_auto]">
        <div
          className={cn(
            "lg:hidden  shadow-xl lg:shadow-none lg:translate-0 shrink-0 fixed top-(--top-panel-height) bg-white z-10 w-full lg:w-[300px] left-0 lg:static transition-transform",
            menuIsOpen ? "translate-y-0" : "-translate-y-[120%]",
          )}
        >
          <SidebarMenu items={menuItems} />
        </div>
        <div className="border-b border-black mb-6 flex flex-col xl:flex-row justify-between gap-4 xl:items-end pb-3">
          <div>
            <h1 className="text-2xl font-medium">Location eintragen</h1>
            <p>
              <TranslationContent
                content={formTranslations?.["formHeader.subtitle"]?.trim()}
              />
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={(e) => {
                // On smaller devices (< xl), show indicators instead of navigating
                if (window.innerWidth < 1280) {
                  e.preventDefault();
                  window.dispatchEvent(
                    new CustomEvent("show-mobile-indicators"),
                  );
                } else {
                  navigate({ to: withLocale("/") });
                }
              }}
            >
              <span className="xl:hidden">Zurück zur Übersicht</span>
              <span className="hidden xl:inline">Zurück zum Dashboard</span>
            </Button>
            <Button type="submit" form="location-form" loading={isSubmitting}>
              {saveButtonLabel}
            </Button>
          </div>
        </div>
        <Alert className="mb-6">
          <InfoIcon className="h-4 w-4" />
          <AlertTitle>Hinweis</AlertTitle>
          <AlertDescription>
            <TranslationContent
              content={formTranslations?.["formHeader.tip"]?.trim()}
            />
          </AlertDescription>
        </Alert>
        <div className="w-full">
          <Outlet />
        </div>
        <Footer
          supportText={footer?.data.supportText}
          phonenumber={footer?.data.phonenumber}
          email={footer?.data.email}
          brandMessage={footer?.data.brandMessage}
          logo={
            footer?.data.logoIdentifier
              ? buildImageUrl(footer.data.logoIdentifier, {
                  size: IMAGE_SIZES.logo,
                })
              : ""
          }
          linksBaseUrl={import.meta.env.VITE_BASE_URL}
          linksUpper={footer?.data.linksUpper || []}
          linksLower={footer?.data.linksLower || []}
        />
      </div>
    </>
  );
}
